import React, { useState } from 'react';
import { Box, Typography, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { NavigateBefore, NavigateNext } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import {
  FieldAnalytics,
  ChoiceFieldAnalytics,
  NumericFieldAnalytics,
  TextFieldAnalytics,
  ListFieldAnalytics,
  SignatureFieldAnalytics,
  MatrixFieldAnalytics,
} from './form-responses-types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    statCard: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '24px',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
    },
    emptyState: {
      backgroundColor: theme.color.grey.neutralBrand100,
      borderRadius: '8px',
      padding: '48px',
      textAlign: 'center',
    },
    emptyStateTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      marginBottom: '8px',
    },
    emptyStateText: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
      maxWidth: '400px',
      margin: '0 auto',
    },
    responseHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
    },
    responseNav: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
  })
);

interface QuestionsTabProps {
  submissionCount: number;
  summaryLoading: boolean;
  fieldAnalytics: FieldAnalytics[];
}

const ChoiceAnalytics: React.FC<{ field: ChoiceFieldAnalytics; theme: any }> = ({ field, theme }) => {
  if (field.data.length === 0) return null;
  return (
    <Box>
      {field.data.map((item) => (
        <Box key={item.fullName} style={{ marginBottom: '8px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={0.5}>
            <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral400 }} title={item.fullName}>
              {item.name}
            </Typography>
            <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300 }}>
              {item.count} ({item.percentage}%)
            </Typography>
          </Box>
          <Box
            style={{
              height: '8px',
              backgroundColor: theme.color.grey.neutralBrand100,
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                height: '100%',
                width: `${item.percentage}%`,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const NumericAnalytics: React.FC<{ field: NumericFieldAnalytics; theme: any }> = ({ field, theme }) => (
  <Box>
    <Box display='flex' alignItems='baseline' marginBottom={2}>
      <Typography style={{ fontSize: '2rem', fontWeight: 600, color: theme.palette.primary.main }}>
        {field.avg}
      </Typography>
      <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300, marginLeft: '8px' }}>
        average (scale {field.scaleMin}-{field.scaleMax})
      </Typography>
    </Box>
    <Box style={{ height: '200px' }}>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={field.data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <XAxis dataKey='name' tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            formatter={(value: number) => [value, 'Responses']}
            contentStyle={{
              backgroundColor: '#fff',
              border: `1px solid ${theme.color.grey.neutralBrand200}`,
              borderRadius: '6px',
              padding: '6px 10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontSize: '0.75rem',
            }}
            labelStyle={{ fontWeight: 500, marginBottom: '2px', fontSize: '0.75rem' }}
            itemStyle={{ fontSize: '0.75rem', padding: 0 }}
          />
          <Bar dataKey='count' fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  </Box>
);

const TextAnalytics: React.FC<{ field: TextFieldAnalytics; theme: any }> = ({ field, theme }) => (
  <Box
    style={{
      maxHeight: '400px',
      overflowY: 'auto',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '12px',
    }}
  >
    {field.items.length > 0 ? (
      field.items.map((item, idx) => (
        <Box
          key={item.id}
          style={{
            padding: '8px 0',
            borderBottom: idx < field.items.length - 1 ? `1px solid ${theme.color.grey.neutralBrand100}` : 'none',
          }}
        >
          <Typography
            style={{
              fontSize: '0.875rem',
              color: theme.color.grey.neutral400,
              whiteSpace: 'pre-wrap',
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))
    ) : (
      <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300 }}>No responses</Typography>
    )}
  </Box>
);

const ListAnalytics: React.FC<{ field: ListFieldAnalytics; theme: any }> = ({ field, theme }) => (
  <Box>
    {field.items.length > 0 ? (
      <Box display='flex' flexWrap='wrap' style={{ gap: '8px' }}>
        {field.items.map((item) => (
          <Box
            key={item.id}
            style={{
              padding: '4px 12px',
              backgroundColor: theme.color.grey.neutralBrand100,
              borderRadius: '16px',
              fontSize: '0.875rem',
              color: theme.color.grey.neutral400,
            }}
          >
            {item.value}
          </Box>
        ))}
      </Box>
    ) : (
      <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300 }}>No responses</Typography>
    )}
  </Box>
);

const SignatureAnalytics: React.FC<{ field: SignatureFieldAnalytics }> = ({ field }) => (
  <Box>
    <Typography style={{ fontSize: '0.875rem', color: '#808080' }}>
      {field.answeredCount} signature{field.answeredCount !== 1 ? 's' : ''} collected
    </Typography>
  </Box>
);

const MatrixAnalytics: React.FC<{ field: MatrixFieldAnalytics; theme: any }> = ({ field, theme }) => (
  <Box
    style={{
      maxHeight: '300px',
      overflowY: 'auto',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '12px',
    }}
  >
    {field.items.length > 0 ? (
      field.items.map((item, idx) => (
        <Box
          key={item.id}
          style={{
            padding: '4px 0',
            borderBottom: idx < field.items.length - 1 ? `1px solid ${theme.color.grey.neutralBrand100}` : 'none',
          }}
        >
          <Typography style={{ fontSize: '0.75rem', color: theme.color.grey.neutral300 }}>{item.value}</Typography>
        </Box>
      ))
    ) : (
      <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300 }}>No responses</Typography>
    )}
  </Box>
);

export const QuestionsTab: React.FC<QuestionsTabProps> = ({ submissionCount, summaryLoading, fieldAnalytics }) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  const totalQuestions = fieldAnalytics.length;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalQuestions - 1;
  const field = fieldAnalytics[currentIndex] || null;

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography className={classes.sectionTitle}>Questions</Typography>
        <Typography className={classes.statLabel}>
          {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {submissionCount === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>No responses yet</Typography>
          <Typography className={classes.emptyStateText}>
            Share your form to start collecting submissions. Question analytics will appear here.
          </Typography>
        </Box>
      ) : summaryLoading ? (
        <CircularProgressOverlay isLoading />
      ) : totalQuestions === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>No questions</Typography>
          <Typography className={classes.emptyStateText}>This form has no question fields to analyze.</Typography>
        </Box>
      ) : field ? (
        <Box className={classes.statCard}>
          <Box className={classes.responseHeader}>
            <Box>
              <Typography className={classes.sectionTitle}>
                Question {currentIndex + 1} of {totalQuestions}
              </Typography>
              <Typography
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: theme.color.grey.neutral400,
                  marginTop: '8px',
                }}
              >
                {field.name}
              </Typography>
              <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginTop: '4px' }}>
                {field.answeredCount} of {field.totalResponses} responded
              </Typography>
            </Box>
            <Box className={classes.responseNav}>
              <TabletButton
                variant='outlined'
                size='small'
                disabled={!hasPrevious}
                onClick={() => setCurrentIndex(currentIndex - 1)}
                style={{
                  borderColor: hasPrevious ? theme.color.grey.neutralBrand300 : theme.color.grey.neutral200,
                  color: hasPrevious ? theme.color.grey.neutral400 : theme.color.grey.neutral200,
                  minWidth: '40px',
                  padding: '6px',
                }}
              >
                <NavigateBefore style={{ fontSize: '20px' }} />
              </TabletButton>
              <TabletButton
                variant='outlined'
                size='small'
                disabled={!hasNext}
                onClick={() => setCurrentIndex(currentIndex + 1)}
                style={{
                  borderColor: hasNext ? theme.color.grey.neutralBrand300 : theme.color.grey.neutral200,
                  color: hasNext ? theme.color.grey.neutral400 : theme.color.grey.neutral200,
                  minWidth: '40px',
                  padding: '6px',
                }}
              >
                <NavigateNext style={{ fontSize: '20px' }} />
              </TabletButton>
            </Box>
          </Box>

          <Box>
            {field.type === 'choice' && <ChoiceAnalytics field={field} theme={theme} />}
            {field.type === 'numeric' && <NumericAnalytics field={field} theme={theme} />}
            {field.type === 'text' && <TextAnalytics field={field} theme={theme} />}
            {field.type === 'list' && <ListAnalytics field={field} theme={theme} />}
            {field.type === 'signature' && <SignatureAnalytics field={field} />}
            {field.type === 'matrix' && <MatrixAnalytics field={field} theme={theme} />}
            {(field.type === 'unknown' || (field.type === 'choice' && field.data.length === 0)) && (
              <Typography className={classes.statLabel}>No responses yet</Typography>
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
